import { endOfDay, startOfDay, subDays } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import DateRangePicker, { RangeType } from 'rsuite/DateRangePicker';
import 'rsuite/DateRangePicker/styles/index.css';
import { Agent } from 'src/interfaces/agent.interface';
import {
  BetweenFilter,
  ScheduledCallFilters,
} from 'src/interfaces/call.interface';
import { WorkflowExecutionThreadStatus } from 'src/interfaces/workflows.interface';
import { getFirstAndLastFourUUID } from 'src/utils/number';
import { AutoCompleteSelectBox } from '../AutoCompleteSelectBox';
import { Button } from '../Button';
import { Input } from '../Input';
import { Label } from '../Label';
import { Modal } from '../Modal';
import { Option, SelectBox } from '../SelectBox';

type ScheduledCallsFiltersModalProps = {
  isOpen: boolean;
  onClose: () => void;
  filters: ScheduledCallFilters;
  onApplyFilters: (filters: ScheduledCallFilters) => void;
  agents: Agent[];
  searchAgents: (name: string) => Promise<{ value: string; label: string }[]>;
  loading: boolean;
};

const ALL_AGENTS_OPTION = { value: 'All Agents', label: 'All Agents' };

const ALL_STATUS_OPTION: Option = {
  label: 'All Statuses',
  value: '',
};

const STATUS_OPTIONS: Option[] = [
  ALL_STATUS_OPTION,
  { label: 'Canceled', value: WorkflowExecutionThreadStatus.CANCELED },
  { label: 'Completed', value: WorkflowExecutionThreadStatus.COMPLETED },
  { label: 'Errored', value: WorkflowExecutionThreadStatus.ERRORED },
  { label: 'Inactive', value: WorkflowExecutionThreadStatus.PAUSED },
  { label: 'Queued', value: WorkflowExecutionThreadStatus.RATE_LIMITED },
  { label: 'Scheduled', value: WorkflowExecutionThreadStatus.RUNNING },
];

type LocalFilters = {
  agent: string;
  status: string;
  contact_number: string;
  from_number: string;
  to_number: string;
  dateRange: [Date, Date];
};

const getDefaultDateRange = (): [Date, Date] => {
  const end = endOfDay(new Date());
  const start = startOfDay(subDays(end, 27));
  return [start, end];
};

const defaultLocalFilters = (): LocalFilters => ({
  agent: ALL_AGENTS_OPTION.value,
  status: ALL_STATUS_OPTION.value,
  contact_number: '',
  from_number: '',
  to_number: '',
  dateRange: getDefaultDateRange(),
});

const parseDateRangeFromFilter = (
  filter?: BetweenFilter,
): [Date, Date] | undefined => {
  if (
    filter?.comparison === 'btwn' &&
    Array.isArray(filter.value) &&
    filter.value.length === 2
  ) {
    const [rangeStart, rangeEnd] = filter.value;
    const start = new Date(rangeStart as string);
    const end = new Date(rangeEnd as string);

    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      return [start, end];
    }
  }

  return undefined;
};

const buildCreatedAtFilter = (
  dateRange?: [Date, Date],
): BetweenFilter | undefined => {
  if (
    dateRange &&
    dateRange[0] instanceof Date &&
    dateRange[1] instanceof Date &&
    !Number.isNaN(dateRange[0].getTime()) &&
    !Number.isNaN(dateRange[1].getTime())
  ) {
    return {
      comparison: 'btwn',
      value: [dateRange[0].toISOString(), dateRange[1].toISOString()] as [
        string,
        string,
      ],
    };
  }
  return undefined;
};

export const ScheduledCallsFiltersModal = ({
  isOpen,
  onClose,
  filters,
  onApplyFilters,
  agents,
  searchAgents,
  loading,
}: ScheduledCallsFiltersModalProps) => {
  const [localFilters, setLocalFilters] = useState<LocalFilters>(
    defaultLocalFilters(),
  );

  useEffect(() => {
    if (!isOpen) return;

    setLocalFilters((prev) => ({
      ...prev,
      agent: filters.agent ?? ALL_AGENTS_OPTION.value,
      status: filters.statuses?.[0] ?? ALL_STATUS_OPTION.value,
      contact_number: filters.contact_number ?? '',
      from_number: filters.from_number ?? '',
      to_number: filters.to_number ?? '',
      dateRange:
        parseDateRangeFromFilter(filters.created_at) ?? getDefaultDateRange(),
    }));
  }, [filters, isOpen]);

  const agentOptions = useMemo(
    () => [
      ALL_AGENTS_OPTION,
      ...agents.map((agent) => ({
        value: agent.id as string,
        label: agent.name || getFirstAndLastFourUUID(agent.id as string),
      })),
    ],
    [agents],
  );

  const handleApply = () => {
    const updatedFilters: ScheduledCallFilters = {};

    if (localFilters.agent && localFilters.agent !== ALL_AGENTS_OPTION.value) {
      updatedFilters.agent = localFilters.agent;
    }

    if (localFilters.contact_number.trim()) {
      updatedFilters.contact_number = localFilters.contact_number.trim();
    }

    if (localFilters.from_number.trim()) {
      updatedFilters.from_number = localFilters.from_number.trim();
    }

    if (localFilters.to_number.trim()) {
      updatedFilters.to_number = localFilters.to_number.trim();
    }

    if (
      localFilters.status &&
      localFilters.status !== ALL_STATUS_OPTION.value
    ) {
      updatedFilters.statuses = [
        localFilters.status as WorkflowExecutionThreadStatus,
      ];
    }

    const createdAtFilter = buildCreatedAtFilter(localFilters.dateRange);
    if (createdAtFilter) {
      updatedFilters.created_at = createdAtFilter;
    }

    onApplyFilters(updatedFilters);
    onClose();
  };

  const handleReset = () => {
    setLocalFilters(defaultLocalFilters());
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Filter Scheduled Calls"
      className="lg:max-w-[800px]"
      actionButton={
        <div className="flex gap-2">
          <Button onClick={handleReset} color="default">
            Reset
          </Button>
          <Button onClick={handleApply} disabled={loading}>
            Apply Filters
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div>
          <Label htmlFor="period">Period</Label>
          <DateRangePicker
            format="MM/dd/yyyy hh:mm aa"
            showMeridiem
            className="w-full"
            value={localFilters.dateRange}
            onChange={(value) => {
              if (!value) return;
              setLocalFilters((prev) => ({
                ...prev,
                dateRange: value as [Date, Date],
              }));
            }}
            ranges={
              [
                {
                  label: 'Last 28 days',
                  value: getDefaultDateRange(),
                  closeOverlay: true,
                },
                {
                  label: 'Last 7 days',
                  value: [
                    startOfDay(subDays(new Date(), 6)),
                    endOfDay(new Date()),
                  ],
                  closeOverlay: true,
                },
                {
                  label: 'Yesterday',
                  value: [
                    startOfDay(subDays(new Date(), 1)),
                    endOfDay(subDays(new Date(), 1)),
                  ],
                  closeOverlay: true,
                },
                {
                  label: 'Today',
                  value: [startOfDay(new Date()), endOfDay(new Date())],
                  closeOverlay: true,
                },
              ] as RangeType[]
            }
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="agent">Agent</Label>
            <AutoCompleteSelectBox
              key={`agent-${localFilters.agent}`}
              defaultValue={agentOptions.find(
                (option) => option.value === localFilters.agent,
              )}
              fetchOptions={searchAgents}
              onChange={(value) =>
                setLocalFilters((prev) => ({ ...prev, agent: value }))
              }
              variant="outlined"
              color="primary"
              size="medium"
              disabled={loading}
              defaultOptions={agentOptions}
              allowRemoveOption={false}
            />
          </div>

          <div>
            <Label htmlFor="status">Status</Label>
            <SelectBox
              options={STATUS_OPTIONS}
              value={STATUS_OPTIONS.find(
                (option) => option.value === localFilters.status,
              )}
              onChange={(value) =>
                setLocalFilters((prev) => ({ ...prev, status: value }))
              }
              variant="outlined"
              color="primary"
              size="medium"
              className="w-full"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Contact Number"
            type="text"
            value={localFilters.contact_number}
            onChange={(e) =>
              setLocalFilters((prev) => ({
                ...prev,
                contact_number: e.target.value,
              }))
            }
          />
        </div>
      </div>
    </Modal>
  );
};
